import React, { useState, useRef, useEffect } from 'react';
import { SendIcon } from './Icons';
import { ImageUploader, CloudinaryUploadService } from './CloudinaryUploader';
import { apiService, useAuth } from '../auth';

// Composant pour afficher la date
const DateSeparator = ({ date }) => (
  <div className="flex items-center justify-center my-4">
    <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
      {date}
    </div>
  </div>
);

// Message Bubble Component
export const MessageBubble = ({ message, onDelete }) => {
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      setIsDeleting(true);
      try {
        await onDelete(message.id);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleLongPress = () => {
    if (message.isOwn) {
      setShowDeleteButton(true);
      // Auto-hide après 3 secondes
      setTimeout(() => setShowDeleteButton(false), 3000);
    }
  };

  return (
    <div className={`flex mb-4 ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`max-w-xs px-4 py-2 rounded-lg relative transition-opacity ${
          isDeleting ? 'opacity-50' : 'opacity-100'
        } ${
          message.isOwn 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-100 text-gray-900'
        }`}
        onMouseEnter={() => message.isOwn && setShowDeleteButton(true)}
        onMouseLeave={() => setShowDeleteButton(false)}
        onTouchStart={handleLongPress}
      >
        {/* Bouton de suppression (seulement pour les messages propres) */}
        {message.isOwn && (showDeleteButton || isDeleting) && (
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className={`absolute -top-2 -right-2 rounded-full w-6 h-6 flex items-center justify-center text-xs z-10 transition-all ${
              isDeleting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-500 hover:bg-red-600 active:scale-95'
            } text-white`}
            title={isDeleting ? 'Suppression...' : 'Supprimer le message'}
          >
            {isDeleting ? '⋯' : '×'}
          </button>
        )}
        
        {/* Affiche l'image si présente */}
        {message.imageUrl && (
          <img
            src={message.imageUrl}
            alt="envoyée"
            className="mb-2 rounded w-full max-w-xs max-h-48 object-cover"
          />
        )}
        
        {/* Affiche le texte si présent */}
        {message.message && (
          <p className="text-sm">{message.message}</p>
        )}
        
        <div className="flex items-center justify-between mt-1">
          <p className={`text-xs ${message.isOwn ? 'text-blue-100' : 'text-gray-500'}`}>
            {message.timestamp}
          </p>
          
          {/* Indicateur de message propre */}
          {message.isOwn && !showDeleteButton && !isDeleting && (
            <span className="text-xs text-blue-200 ml-2" title="Appuyez longuement pour supprimer">
              ✓
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Chat Input Component
export const ChatInput = ({ conversationId, onSend, onSendImage }) => {
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState('environment');
  const fileInputRef = useRef();
  const videoRef = useRef();

  // Détecter si on est sur mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Debug des props et refs
  useEffect(() => {
    console.log('ChatInput - Props reçues:');
    console.log('- conversationId:', conversationId);
    console.log('- onSend:', typeof onSend);
    console.log('- onSendImage:', typeof onSendImage);
    console.log('- isMobile:', isMobile);
  }, [conversationId, onSend, onSendImage, isMobile]);

  // Nettoyage du stream caméra au démontage
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Ouvre directement l'appareil photo (caméra arrière par défaut)
  const handleCameraClick = () => {
    console.log('Ouverture directe de l\'appareil photo (caméra arrière)');
    setCurrentFacingMode('environment');
    setShowCamera(true);
    startCamera('environment');
  };

  // Change vers la caméra frontale (pour mobile seulement)
  const handleSwitchToFrontCamera = () => {
    console.log('Basculement vers caméra frontale');
    setCurrentFacingMode('user');
    if (showCamera) {
      // Si la caméra est déjà ouverte, on change de caméra
      startCamera('user');
    } else {
      // Sinon on ouvre la caméra frontale
      setShowCamera(true);
      startCamera('user');
    }
  };

  // Démarre la caméra avec aperçu
  const startCamera = async (facingMode) => {
    try {
      // Arrêter le stream précédent s'il existe
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
    } catch (error) {
      console.error('Erreur accès caméra:', error);
      alert('Impossible d\'accéder à la caméra: ' + error.message);
      setShowCamera(false);
    }
  };

  // Ferme la caméra
  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Capture une photo depuis la vidéo
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convertir en fichier
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      console.log('Photo capturée:', file.name, file.type);
      
      // Fermer la caméra
      closeCamera();
      
      // Upload et envoi
      await uploadAndSendImage(file);
    }, 'image/jpeg', 0.9);
  };

  
  // Ouvre le file picker
  const handleFilePicker = () => {
    console.log('Option fichier choisie');
    console.log('fileInputRef.current:', fileInputRef.current);
    console.log('fileInputRef type:', typeof fileInputRef.current);
    
    if (fileInputRef.current) {
      console.log('Clic sur input file - déclenchement...');
      try {
        fileInputRef.current.click();
        console.log('Click() exécuté avec succès');
        
      } catch (error) {
        console.error('Erreur lors du click():', error);
        alert('Erreur lors de l\'ouverture du sélecteur de fichier: ' + error.message);
      }
    } else {
      console.error('fileInputRef.current est null');
      alert('Erreur: impossible d\'ouvrir le sélecteur de fichier');
    }
  };

  // Sélection fichier → Upload + Envoi direct
  const handleFileChange = async (e) => {
    console.log('handleFileChange déclenché:', e.target.files);
    console.log('Event target:', e.target);
    console.log('Files array length:', e.target.files?.length);
    
    const file = e.target.files[0];
    if (file) {
      console.log('Fichier sélectionné:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      
      // Vérifier que le fichier est bien une image
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image');
        return;
      }
      
      // Vérifier la taille du fichier (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Le fichier est trop volumineux (max 10MB)');
        return;
      }
      
      await uploadAndSendImage(file);
    } else {
      console.log('Aucun fichier sélectionné dans e.target.files[0]');
    }
    
    // Reset l'input pour permettre de sélectionner le même fichier à nouveau
    e.target.value = '';
  };

  // Upload + Envoi image (fichier ou caméra)
  const uploadAndSendImage = async (file) => {
    console.log('Début upload image:', file);
    console.log('onSendImage disponible:', typeof onSendImage);
    
    if (!onSendImage) {
      console.error('onSendImage non défini - impossible de continuer');
      alert('Erreur: fonction d\'envoi non disponible');
      return;
    }
    
    setUploading(true);
    
    try {
      // Utilise le service Cloudinary via l'API backend (plus fiable)
      console.log('Upload via API backend...');
      await uploadViaBackend(file);
    } catch (e) {
      console.error('Erreur upload/envoi:', e);
      alert("Erreur lors de l'envoi de l'image: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  // Upload via l'API backend (méthode principale)
  const uploadViaBackend = async (file) => {
    console.log('Upload via backend - fichier:', file.name, file.type, file.size);
    
    try {
      // Utilise le service CloudinaryUploadService du CloudinaryUploader
      const result = await CloudinaryUploadService.uploadViaBackend(file, { folder: 'messages' });
      
      if (result.success && result.data) {
        console.log('Upload backend réussi:', result.data);
        
        // Utilise l'URL optimisée pour l'affichage
        const imageUrl = result.data.original || result.data.url;
        console.log('URL image obtenue:', imageUrl);
        
        await onSendImage(imageUrl);
        return;
      } else {
        throw new Error(result.error || 'Échec upload backend');
      }
      
    } catch (e) {
      console.error('Erreur upload backend:', e);
      
      // Si le backend échoue, on utilise le fallback base64
      console.log('Backend échoué, utilisation de base64...');
      await uploadAsBase64(file);
    }
  };

  // Upload direct vers Cloudinary (méthode principale)
  const uploadToCloudinaryDirect = async (file) => {
    console.log('Upload direct vers Cloudinary - fichier:', file.name, file.type, file.size);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Essayons plusieurs configurations Cloudinary
      const configs = [
        // 1. Sans preset (unsigned upload)
        { preset: null, url: 'https://api.cloudinary.com/v1_1/dfdpnuq3b/image/upload' },
        // 2. Avec preset ml_default
        { preset: 'ml_default', url: 'https://api.cloudinary.com/v1_1/dfdpnuq3b/image/upload' },
        // 3. Essai avec d'autres presets courants
        { preset: 'unsigned_preset', url: 'https://api.cloudinary.com/v1_1/dfdpnuq3b/image/upload' },
        { preset: 'default', url: 'https://api.cloudinary.com/v1_1/dfdpnuq3b/image/upload' }
      ];
      
      for (const config of configs) {
        try {
          console.log(`Essai Cloudinary avec ${config.preset ? 'preset: ' + config.preset : 'unsigned'}...`);
          
          const configFormData = new FormData();
          configFormData.append('file', file);
          if (config.preset) {
            configFormData.append('upload_preset', config.preset);
          }
          
          const response = await fetch(config.url, {
            method: 'POST',
            body: configFormData
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Résultat upload Cloudinary:', data);
            
            if (data.secure_url) {
              console.log('URL image obtenue:', data.secure_url);
              await onSendImage(data.secure_url);
              return; // Succès, on sort de la fonction
            }
          } else {
            const errorData = await response.text();
            console.warn(`Config ${config.preset || 'unsigned'} échouée:`, response.status, errorData);
          }
        } catch (configError) {
          console.warn(`Erreur config ${config.preset || 'unsigned'}:`, configError.message);
        }
      }
      
      // Si toutes les configs Cloudinary échouent
      throw new Error('Toutes les configurations Cloudinary ont échoué');
      
    } catch (e) {
      console.error('Erreur upload direct:', e);
      
      // Si Cloudinary échoue complètement, essayons un service alternatif
      console.log('Cloudinary échoué, essai avec service alternatif...');
      try {
        await uploadToImgBB(file);
      } catch (imgbbError) {
        console.log('Services externes échoués, utilisation de base64...');
        await uploadAsBase64(file);
      }
    }
  };

  // Upload vers ImgBB (service alternatif gratuit)
  const uploadToImgBB = async (file) => {
    console.log('Upload vers ImgBB...');
    
    const formData = new FormData();
    formData.append('image', file);
    
    // Utilisons une API alternative gratuite - postimages.org
    try {
      const response = await fetch('https://postimages.org/api/upload', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`PostImages erreur: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Résultat PostImages:', data);
      
      if (data.url) {
        console.log('URL PostImages obtenue:', data.url);
        await onSendImage(data.url);
        return;
      }
    } catch (postError) {
      console.warn('PostImages échoué:', postError);
    }
    
    // Si PostImages échoue, essayons avec une méthode simple de dataURL
    console.log('Essai avec service de stockage temporaire...');
    
    // Pour l'instant, utilisons base64 directement
    throw new Error('Services externes non disponibles');
  };

  // Méthode de fallback: convertir en base64 et envoyer directement
  const uploadAsBase64 = async (file) => {
    console.log('Conversion en base64...');
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const base64 = e.target.result;
          console.log('Base64 généré, taille:', base64.length);
          
          // Envoi du base64 comme URL d'image
          await onSendImage(base64);
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  const handleSend = () => {
    if (message.trim()) {
      onSend(message);
      setMessage('');
    }
  };

  return (
    <div className="border-t border-gray-200 p-4 bg-white">
      {/* Interface caméra avec aperçu */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative w-full h-full max-w-md max-h-96 bg-black rounded-lg overflow-hidden">
            {/* Vidéo aperçu */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Contrôles caméra */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              {/* Bouton fermer */}
              <button
                onClick={closeCamera}
                className="p-3 rounded-full bg-gray-600 text-white hover:bg-gray-700"
                title="Fermer"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Bouton capture */}
              <button
                onClick={capturePhoto}
                disabled={uploading}
                className="p-4 rounded-full bg-white text-black hover:bg-gray-200 disabled:opacity-50"
                title="Prendre la photo"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              
              {/* Bouton changer caméra (mobile seulement) */}
              {isMobile && (
                <button
                  onClick={() => startCamera(currentFacingMode === 'environment' ? 'user' : 'environment')}
                  className="p-3 rounded-full bg-gray-600 text-white hover:bg-gray-700"
                  title="Changer de caméra"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Indicateur d'envoi */}
      {uploading && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-blue-600 text-sm">📤 Envoi de l'image...</span>
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* Input file caché pour sélection d'images */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Bouton upload principal (icône upload) */}
        <button
          type="button"
          onClick={handleFilePicker}
          className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600"
          title="Ajouter une image"
          disabled={uploading}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>

        {/* Bouton caméra (caméra arrière par défaut) */}
        <button
          type="button"
          onClick={handleCameraClick}
          className="p-2 rounded-full bg-green-100 hover:bg-green-200 text-green-600"
          title="Prendre une photo (caméra arrière)"
          disabled={uploading}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        {/* Bouton pour changer vers caméra frontale (mobile seulement) */}
        {isMobile && (
          <button
            type="button"
            onClick={handleSwitchToFrontCamera}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
            title="Caméra frontale"
            disabled={uploading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>
        )}

        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tapez votre message..."
          className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          disabled={uploading}
        />
        <button
          onClick={handleSend}
          disabled={!message.trim() || uploading}
          className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Envoyer le message"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const Conversation = ({ conversationId, messages: externalMessages, setMessages: setExternalMessages, listingId, setConversationId }) => {
  
  const [messages, setMessages] = useState(externalMessages || []);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Chargement des messages depuis l'API
  useEffect(() => {
    const loadMessages = async () => {
      if (!conversationId) return;
      
      setLoading(true);
      try {
        const messagesData = await apiService.getConversationMessages(conversationId);
        console.log('Messages chargés:', messagesData);
        
        // Transformer les messages API en format d'affichage
        const formattedMessages = messagesData.map(msg => ({
          id: msg.id,
          message: msg.message_type === 'text' ? msg.content : null,
          imageUrl: msg.message_type === 'image' ? msg.content : null,
          isOwn: msg.sender_id === user?.id,
          timestamp: new Date(msg.created_at).toLocaleTimeString(),
          created_at: msg.created_at // Garder la date complète pour le regroupement
        }));
        
        setMessages(formattedMessages);
        
        // Marquer la conversation comme lue
        await apiService.markConversationRead(conversationId);
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [conversationId, user]);

  // Synchroniser avec les messages externes si fournis
  useEffect(() => {
    if (externalMessages && externalMessages.length > 0) {
      setMessages(externalMessages);
    }
  }, [externalMessages]);
  
  // Fonction pour envoyer un message texte
  const handleSend = async (text) => {
    console.log('handleSend appelé avec:', text);
    
    if (!conversationId) {
      console.error('Pas de conversation active');
      return;
    }

    try {
      const result = await apiService.sendMessage(conversationId, text, 'text');
      if (result.success) {
        const newMsg = {
          id: result.data.id || Date.now(),
          message: text,
          imageUrl: null,
          isOwn: true,
          timestamp: new Date().toLocaleTimeString(),
          created_at: new Date().toISOString() // Ajouter created_at pour le regroupement
        };
        
        setMessages(prev => [...prev, newMsg]);
        if (setExternalMessages) setExternalMessages(prev => [...prev, newMsg]);
      } else {
        alert('Erreur lors de l\'envoi: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi du message');
    }
  };

  // Envoi d'image avec création auto de conversation si besoin
  const handleSendImage = async (imageUrl) => {
    console.log('handleSendImage appelé avec URL:', imageUrl);
    
    try {
      let convId = conversationId;
      
      // Si pas de conversation, on la crée
      if (!convId) {
        if (!listingId) {
          console.error('listingId manquant');
          alert("Impossible de créer la conversation : listingId manquant");
          return;
        }
        
        console.log('Création conversation pour listing:', listingId);
        const resConv = await apiService.createConversation(listingId);
        console.log('Résultat création conversation:', resConv);
        
        if (resConv.success && resConv.data && resConv.data.id) {
          convId = resConv.data.id;
          if (setConversationId) setConversationId(convId);
        } else {
          console.error('Échec création conversation:', resConv);
          alert("Impossible de créer la conversation");
          return;
        }
      }
      
      console.log('Envoi message image vers conversation:', convId);
      // Envoie le message image avec le bon format d'API
      const resMsg = await apiService.sendMessage(convId, imageUrl, 'image');
      console.log('Résultat envoi message:', resMsg);
      
      if (resMsg.success) {
        // Ajoute le message image à la conversation
        const newMsg = {
          id: resMsg.data.id || Date.now(),
          imageUrl: resMsg.data.content || imageUrl,
          message: null,
          isOwn: true,
          timestamp: new Date().toLocaleTimeString(),
          created_at: new Date().toISOString() // Ajouter created_at pour le regroupement
        };
        console.log('Nouveau message ajouté:', newMsg);
        setMessages(prev => [...prev, newMsg]);
        if (setExternalMessages) setExternalMessages(prev => [...prev, newMsg]);
      } else {
        console.error('Échec envoi message:', resMsg);
        alert("Échec de l'envoi du message");
      }
    } catch (e) {
      console.error('Erreur dans handleSendImage:', e);
      alert("Erreur lors de l'envoi: " + e.message);
    }
  };

  // Debug des fonctions avant le rendu
  console.log('Conversation render - handleSend:', typeof handleSend);
  console.log('Conversation render - handleSendImage:', typeof handleSendImage);

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Réinitialiser les heures pour la comparaison
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (messageDate.getTime() === todayDate.getTime()) {
      return "Aujourd'hui";
    } else if (messageDate.getTime() === yesterdayDate.getTime()) {
      return "Hier";
    } else {
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Fonction pour regrouper les messages par date
  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentDate = null;

    messages.forEach((msg, index) => {
      const msgDate = msg.created_at || new Date().toISOString();
      const msgDateOnly = new Date(msgDate).toDateString();

      if (currentDate !== msgDateOnly) {
        currentDate = msgDateOnly;
        grouped.push({
          type: 'date',
          date: formatDate(msgDate),
          key: `date-${msgDateOnly}`
        });
      }

      grouped.push({
        type: 'message',
        ...msg,
        key: msg.id || `msg-${index}`
      });
    });

    return grouped;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Chargement des messages...</p>
            </div>
          </div>
        ) : (
          groupMessagesByDate(messages).map(item => {
            if (item.type === 'date') {
              return <DateSeparator key={item.key} date={item.date} />;
            } else {
              return <MessageBubble key={item.key} message={item} />;
            }
          })
        )}
      </div>
      <ChatInput conversationId={conversationId} onSend={handleSend} onSendImage={handleSendImage} />
    </div>
  );
};
